import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { auth, db } from '../firebase-config';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { LEGAL_URLS } from '../config/legalUrls';
import './PaymentSuccess.css';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  
  // Immediate redirect to payment-success-setup with all params
  return <Navigate to={`/payment-success-setup?${searchParams.toString()}`} replace />;
  
  /* Fallback code - unreachable but preserved for reference
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Helper function to determine user role
  const getUserRole = () => {
    if (isPro) {
      return 'user_pro';
    } else if (isDynamic) {
      return tier === 'tier3' ? 'user_dynamic_3' : 'user_dynamic_2';
    } else {
      return tier === 'tier3' ? 'user_paid_static_3' : 'user_paid_static_2';
    }
  };
  
  // Get payment details from URL params
  const sessionId = searchParams.get('session_id');
  const tier = searchParams.get('tier') || 'tier2';
  const isDynamic = searchParams.get('isDynamic') === 'true';
  const isPro = searchParams.get('isPro') === 'true';
  const tierName = isDynamic ? 'ADU Lab Dynamic' : 'ADU Lab Report';
  const emailFromUrl = searchParams.get('email');
  const fromAssessment = searchParams.get('fromAssessment') === 'true';
  
  useEffect(() => {
    // Account is already created by Stripe webhook
    // If user is not logged in, they need to check their email for login instructions
    if (!currentUser) {
      // Pre-fill email if provided
      if (emailFromUrl) {
        setEmail(decodeURIComponent(emailFromUrl));
      }
      // Don't show account form - account already exists from webhook
      setShowAccountForm(false);
    }
  }, [currentUser, emailFromUrl, sessionId]);
  
  const fetchCheckoutDetails = async () => {
    try {
      const response = await fetch('/api/getCheckoutSession', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId })
      });
      
      const data = await response.json();
      if (data.success) {
        // Pre-populate form fields with Stripe data
        if (data.customerName) {
          setFullName(data.customerName);
        }
        if (data.customerEmail && !email) {
          setEmail(data.customerEmail);
        }
      }
    } catch (error) {
      console.error('Error fetching checkout details:', error);
      // Don't show error to user, just continue without pre-population
    }
  };
  
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Create Firebase auth account
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create/update user document
      await setDoc(doc(db, 'users', result.user.uid), {
        email: result.user.email,
        fullName: fullName || result.user.displayName || '',
        role: getUserRole(),
        createdAt: serverTimestamp(),
        paymentSessionId: sessionId
      }, { merge: true });
      
      // Wait for auth state to propagate
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Continue to questionnaire
      navigateToQuestionnaire();
    } catch (err) {
      console.error('Account creation error:', err);
      setError(err.message);
      setLoading(false);
    }
  };
  
  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Create/update user document
      await setDoc(doc(db, 'users', result.user.uid), {
        email: result.user.email,
        fullName: result.user.displayName || '',
        role: getUserRole(),
        createdAt: serverTimestamp(),
        paymentSessionId: sessionId
      }, { merge: true });
      
      // Continue to questionnaire
      navigateToQuestionnaire();
    } catch (err) {
      console.error('Google sign in error:', err);
      setError('Failed to sign in with Google');
      setLoading(false);
    }
  };
  
  const handleAppleSignIn = async () => {
    setLoading(true);
    setError('');
    
    try {
      const provider = new OAuthProvider('apple.com');
      const result = await signInWithPopup(auth, provider);
      
      // Create/update user document
      await setDoc(doc(db, 'users', result.user.uid), {
        email: result.user.email,
        fullName: result.user.displayName || '',
        role: getUserRole(),
        createdAt: serverTimestamp(),
        paymentSessionId: sessionId
      }, { merge: true });
      
      // Continue to questionnaire
      navigateToQuestionnaire();
    } catch (err) {
      console.error('Apple sign in error:', err);
      setError('Failed to sign in with Apple');
      setLoading(false);
    }
  };
  
  const navigateToQuestionnaire = () => {
    // For paid users, bypass email verification requirement and go directly to Studio
    // They'll see the email verification banner but can still use the app
    navigate('/studio', { replace: true });
  };
  
  return (
    <div className="payment-success-container">
      <main className="container">
        <div className="success-card">
          <header className="success-header">
            <div className="header-text">
              <div className="header-title-wrapper">
                <h1>Analysis Engine Activated!</h1>
                <div className="pulse-pill">
                  <div className="pulse-bar"></div>
                </div>
              </div>
              <p>Your purchase is complete and your ADU Lab account is ready.</p>
            </div>
          </header>
          
          {!currentUser ? (
            <section className="account-creation-section">
              <h2 className="section-title">Welcome to ADU Lab!</h2>
              <p className="section-subtitle">Your account has been created. Check your email for login instructions.</p>
              
              {error && <div className="error-message">{error}</div>}
              
              <form onSubmit={handleCreateAccount} className="account-form">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    disabled={loading}
                    placeholder="John Smith"
                  />
                </div>
                
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    readOnly={!!sessionId || !!emailFromUrl}
                    placeholder="john@example.com"
                    className={sessionId || emailFromUrl ? 'readonly-field' : ''}
                  />
                  {(sessionId || emailFromUrl) && <p className="text-xs text-gray-500 mt-1">Email locked from {sessionId ? 'payment transaction' : 'assessment'}</p>}
                </div>
                
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    placeholder="••••••••"
                    minLength="6"
                    title="Password must be at least 6 characters"
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account & Continue'}
                </button>
              </form>
              
              <div className="divider">or</div>
              
              <div className="social-buttons">
                <button 
                  className="btn btn-google"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
                  <span>Sign up with Google</span>
                </button>
                
                <button 
                  className="btn btn-apple"
                  onClick={handleAppleSignIn}
                  disabled={loading}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M15.645 13.619c-.297.789-.661 1.513-1.092 2.173-.588.899-1.067 1.52-1.439 1.865-.574.572-1.19.865-1.85.881-.474 0-1.045-.135-1.709-.407-.667-.271-1.28-.405-1.841-.405-.589 0-1.22.134-1.895.405-.676.272-1.221.413-1.64.426-.634.025-1.267-.252-1.896-.831-.402-.372-.903-1.009-1.502-1.911-.644-.973-1.173-2.102-1.588-3.388C.062 10.987 0 9.594 0 8.258c0-1.512.327-2.817.98-3.911.514-.883 1.198-1.58 2.055-2.093C3.89 1.742 4.81 1.48 5.8 1.467c.495 0 1.144.153 1.952.456.805.303 1.322.457 1.549.457.169 0 .743-.18 1.716-.539.92-.333 1.697-.471 2.336-.416 1.726.139 3.023.82 3.888 2.048-1.544.935-2.307 2.245-2.291 3.925.015 1.309.489 2.399 1.42 3.266.424.409.898.725 1.425.952l-.15.003zM13.088 0c.023.136.034.273.034.41 0 1.029-.375 1.989-1.122 2.876-.901 1.058-1.992 1.668-3.174 1.572a3.168 3.168 0 01-.024-.387c0-.987.43-2.044 1.194-2.907.381-.438.866-.802 1.453-1.091C12.035.164 12.595.012 13.134 0h-.046z"/>
                  </svg>
                  <span>Sign up with Apple</span>
                </button>
              </div>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                By creating an account, you agree to our{' '}
                <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Privacy Policy
                </a>
              </p>
            </section>
          ) : (
            <>
              <section className="whats-next-section">
                <h2 className="section-title">What's Next?</h2>
                <div className="next-steps">
                  <div className="step">
                    <div className="step-number">1</div>
                    <div className="step-text">Check your email for an account confirmation and receipt.</div>
                  </div>
                  <div className="step">
                    <div className="step-number">2</div>
                    <div className="step-text">Access your Dynamic workspace to start working on your project.</div>
                  </div>
                  <div className="step">
                    <div className="step-number">3</div>
                    <div className="step-text">Use the Hub tools to build and manage your ADU project.</div>
                  </div>
                </div>
              </section>
              
              <section className="purchase-summary">
                <h3>Your {tierName} is Ready</h3>
                <p>We've applied your purchase to your account. You can now access all the features of your plan.</p>
              </section>
              
              <button 
                onClick={navigateToQuestionnaire} 
                className="btn btn-primary"
                style={{ width: '100%' }}
              >
                Go to Dynamic Workspace
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
  */
};

export default PaymentSuccess;
